import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment.prod';

import {
  ApiUser,
  ApiUserProfile,
} from 'src/app/modules/org-profile/models/org.models';

/**
 * The logged-in user's own account + personal-details records.
 *
 * Two separate resources back the profile page:
 *   - /user/users/{id}/                account/role/reporting-line fields
 *   - /user/profile/user-profile/{id}/ personal details (name, address,
 *     phone, CTC…), linked back to the account via its own `user` field
 *
 * There's no "get my profile record by user id" endpoint, so — same as
 * the candidate-profile page already does against this exact API — the
 * profile is found by fetching the list and matching `user` client-side.
 */
@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(private http: HttpClient) {}

  /** GET /user/users/{id}/ */
  getUser(id: number): Observable<ApiUser> {
    return this.http.get<ApiUser>(`${environment._url}/user/users/${id}/`);
  }

  /** PATCH /user/users/{id}/ */
  updateUser(id: number, payload: Partial<ApiUser>): Observable<ApiUser> {
    return this.http.patch<ApiUser>(
      `${environment._url}/user/users/${id}/`,
      payload,
    );
  }

  /** GET /user/users/?organization={id} — used to source the reporting-manager dropdown. */
  getOrganizationUsers(organizationId: number): Observable<ApiUser[]> {
    return this.http.get<ApiUser[]>(`${environment._url}/user/users/`, {
      params: { organization: String(organizationId) },
    });
  }

  /** GET /user/profile/user-profile/, matched down to the one row for this user. */
  getUserProfileByUserId(userId: number): Observable<ApiUserProfile | null> {
    return this.http
      .get<ApiUserProfile[]>(`${environment._url}/user/profile/user-profile/`)
      .pipe(
        map((list) => (list || []).find((p) => p.user === userId) || null),
      );
  }

  /**
   * PATCH /user/profile/user-profile/{profileId}/ — the profile row already
   * exists. Takes FormData rather than a plain object so a new
   * profile_picture File can ride along in the same request — same
   * multipart convention the candidate-profile page already uses against
   * this exact endpoint.
   */
  updateUserProfile(
    profileId: number,
    payload: FormData,
  ): Observable<ApiUserProfile> {
    return this.http.patch<ApiUserProfile>(
      `${environment._url}/user/profile/user-profile/${profileId}/`,
      payload,
    );
  }

  /** POST /user/profile/user-profile/ — first-time save, no profile row exists yet. */
  createUserProfile(payload: FormData): Observable<ApiUserProfile> {
    return this.http.post<ApiUserProfile>(
      `${environment._url}/user/profile/user-profile/`,
      payload,
    );
  }
}
